/**
 * Audio capture hook for live voice chat.
 *
 * Uses AudioWorklet for efficient real-time audio processing.
 * Captures microphone audio at 16kHz mono PCM format.
 */

import { useRef, useState, useCallback } from 'react'

// Audio configuration for Gemini Live
const SAMPLE_RATE = 16000
const CHANNELS = 1

export interface AudioCaptureOptions {
  /** Callback for each audio chunk (Int16Array of PCM samples) */
  onChunk: (pcm: ArrayBuffer) => void
  /** Callback when capture starts */
  onStart?: () => void
  /** Callback when capture stops */
  onStop?: () => void
  /** Callback on error */
  onError?: (error: Error) => void
}

export interface AudioCaptureState {
  isRecording: boolean
  isSupported: boolean
  error: string | null
}

export interface AudioCaptureControls {
  start: () => Promise<void>
  stop: () => void
}

/**
 * Convert Float32Array audio samples to Int16Array PCM.
 * This is the format expected by Gemini's audio API.
 */
function float32ToPcm16(float32: Float32Array): Int16Array {
  const pcm16 = new Int16Array(float32.length)
  for (let i = 0; i < float32.length; i++) {
    // Clamp to [-1, 1] and scale to Int16 range
    const s = Math.max(-1, Math.min(1, float32[i]))
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return pcm16
}

/**
 * Hook for capturing microphone audio for live voice chat.
 *
 * @example
 * ```tsx
 * const { isRecording, start, stop } = useAudioCapture({
 *   onChunk: (pcm) => connection.sendAudio(pcm),
 *   onError: (err) => console.error(err),
 * })
 *
 * // Start recording
 * await start()
 *
 * // Stop recording
 * stop()
 * ```
 */
export function useAudioCapture(options: AudioCaptureOptions): AudioCaptureState & AudioCaptureControls {
  const { onChunk, onStart, onStop, onError } = options

  const [isRecording, setIsRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const audioContextRef = useRef<AudioContext | null>(null)
  const workletRef = useRef<AudioWorkletNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)

  // Check for AudioWorklet support
  const isSupported = typeof AudioWorkletNode !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined'

  const start = useCallback(async () => {
    if (isRecording) return
    if (!isSupported) {
      const err = new Error('AudioWorklet not supported in this browser')
      setError(err.message)
      onError?.(err)
      return
    }

    try {
      setError(null)

      // Create AudioContext at target sample rate
      const ctx = new AudioContext({ sampleRate: SAMPLE_RATE })
      audioContextRef.current = ctx

      // Load the AudioWorklet processor
      await ctx.audioWorklet.addModule('/audio-worklet.js')

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: CHANNELS,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      streamRef.current = stream

      // Create source from microphone
      const source = ctx.createMediaStreamSource(stream)
      sourceRef.current = source

      // Create AudioWorklet node
      const worklet = new AudioWorkletNode(ctx, 'pcm-processor', {
        channelCount: CHANNELS,
        numberOfInputs: 1,
        numberOfOutputs: 0,
      })
      workletRef.current = worklet

      // Handle audio chunks from worklet
      worklet.port.onmessage = (event) => {
        const float32Data = event.data as Float32Array
        const pcm16 = float32ToPcm16(float32Data)
        onChunk(pcm16.buffer)
      }

      // Connect: microphone -> worklet
      source.connect(worklet)

      setIsRecording(true)
      onStart?.()

    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error.message)
      onError?.(error)
      // Cleanup on error
      stop()
    }
  }, [isRecording, isSupported, onChunk, onStart, onError])

  const stop = useCallback(() => {
    // Disconnect worklet
    if (workletRef.current) {
      workletRef.current.disconnect()
      workletRef.current = null
    }

    // Disconnect source
    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }

    // Stop media stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // Ignore close errors
      })
      audioContextRef.current = null
    }

    setIsRecording(false)
    onStop?.()
  }, [onStop])

  return {
    isRecording,
    isSupported,
    error,
    start,
    stop,
  }
}

export default useAudioCapture
